import { Component, computed, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { StylingService } from '../../services/styling/styling.service';
import { MatCard } from '@angular/material/card';
import { ISystemAlert, SystemAlertsService } from '../../services/system-alerts/system-alerts.service';
import { Subscription } from 'rxjs';

const MAT_IMPORTS = [
  MatToolbar,
  MatIconButton,
  MatIcon,
  MatCard,
  MatFormField,
  MatInput,
  MatTooltip,
];

type NotificationSortDirection = 'asc' | 'desc';

@Component({
  selector: 'lh-alert-panel',
  standalone: true,
  imports: [...MAT_IMPORTS, DatePipe],
  templateUrl: './alert-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './alert-panel.component.scss'
})
export class AlertPanelComponent implements OnDestroy {
  private service = inject(StylingService);
  private systemAlertsService = inject(SystemAlertsService);

  private notificationSubscription: Subscription | undefined;

  notifications = signal<ISystemAlert[]>([]);
  searchTerm = signal<string>('');
  sortDirection = signal<NotificationSortDirection>('desc');

  sortIcon = computed(() => this.sortDirection() === 'asc' ? 'clock_arrow_up' : 'clock_arrow_down');
  sortLabel = computed(() => this.sortDirection() === 'asc' ? 'Oldest first' : 'Newest first');

  displayedNotifications = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const direction = this.sortDirection();
    const list = this.notifications();

    const filtered = term
      ? list.filter((notification) => this.matchesSearch(notification, term))
      : [...list];

    filtered.sort((a, b) => this.compareTimestamps(a, b, direction));
    return filtered;
  });

  constructor() {
    this.notificationSubscription = this.systemAlertsService.notifications.subscribe((notification) => {
      this.notifications.update((list) => [...list, notification]);
    });
  }

  ngOnDestroy(): void {
    if (this.notificationSubscription != null) {
      this.notificationSubscription.unsubscribe();
    }
  }

  close() {
    this.service.toggleAlertPanel(false);
  }

  toggleSortDirection() {
    this.sortDirection.update((direction) => direction === 'asc' ? 'desc' : 'asc');
  }

  updateSearchTerm(event: Event) {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.searchTerm.set(value);
  }

  private matchesSearch(notification: ISystemAlert, term: string): boolean {
    const searchable = [
      notification.site_name,
      notification.camera_name,
      notification.timestamp,
    ]
      .filter((value): value is string => value != null)
      .join(' ')
      .toLowerCase();

    return searchable.includes(term);
  }

  private compareTimestamps(a: ISystemAlert, b: ISystemAlert, direction: NotificationSortDirection): number {
    const aTime = this.toTimestamp(a.timestamp);
    const bTime = this.toTimestamp(b.timestamp);
    const aInvalid = !Number.isFinite(aTime);
    const bInvalid = !Number.isFinite(bTime);

    if (aInvalid && bInvalid) {
      return 0;
    }

    if (aInvalid) {
      return 1;
    }

    if (bInvalid) {
      return -1;
    }

    if (direction === 'asc') {
      return aTime - bTime;
    }

    return bTime - aTime;
  }

  private toTimestamp(timestamp: string | undefined): number {
    if (!timestamp) {
      return Number.POSITIVE_INFINITY;
    }
    const isoTimestamp = timestamp.replace(' ', 'T');
    const parsed = new Date(isoTimestamp).getTime();
    return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  }
}
